import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  TrendingDown,
  CheckCircle2,
  Coins,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import NwflInboundLink from "./NwflInboundLink";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import CalculatorCallout from "./CalculatorCallout";
import { underpaidClaims } from "../data/underpaidClaims";
import { businessInfo } from "../mock";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "../utils/schemas";

const PILLAR = "/underpaid-insurance-claim-florida";

const UnderpaidClaimPage = () => {
  const { slug } = useParams();
  const page = underpaidClaims[slug];
  if (!page) return <Navigate to="/" replace />;

  const siblings = Object.values(underpaidClaims).filter((p) => p.slug !== slug);
  const schemas = [
    localBusinessSchema(),
    faqSchema(page.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Underpaid Insurance Claims", path: PILLAR },
      { name: `Underpaid ${page.peril} Claim`, path: `/${page.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs
        items={[
          { name: "Underpaid Insurance Claims", path: PILLAR },
          { name: `Underpaid ${page.peril} Claim`, path: `/${page.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              Underpaid Claims · {page.peril}
            </span>
          </div>
          <h1
            className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.h1}
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              data-testid="underpaid-cta-review"
              className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Recover My Shortfall — Free Review</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Why underpaid */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Why {page.peril} Claims Get Underpaid in Florida
          </h2>
          <ul className="mt-7 space-y-3">
            {page.underpayReasons.map((r, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4"
              >
                <TrendingDown className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How we recover the shortfall */}
      <section className="py-14 md:py-16 bg-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            How We Recover Your {page.peril} Shortfall
          </h2>
          <div className="mt-9 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", t: "Audit the Payment", d: "We compare the carrier's scope and pricing against the true cost of repair, line by line." },
              { n: "02", t: "Re-document", d: "We re-inspect and document the hidden, omitted, and underscoped damage the first estimate missed." },
              { n: "03", t: "Rebuild the Estimate", d: "We write an independent replacement-cost estimate at current Florida market rates." },
              { n: "04", t: "Supplement & Negotiate", d: "We file a formal supplement and negotiate — escalating to appraisal or mediation if needed." },
            ].map((s) => (
              <div key={s.n} className="bg-[#0F0F0F] p-6">
                <div
                  className="text-[#E11D26] text-4xl font-black leading-none"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {s.n}
                </div>
                <h3
                  className="mt-4 text-white font-black uppercase text-sm tracking-wide"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {s.t}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={PILLAR}
              data-testid="underpaid-pillar-link"
              className="inline-flex items-center gap-2 bg-[#E11D26]/10 border border-[#E11D26]/50 hover:bg-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
            >
              <Coins className="h-4 w-4" /> Underpaid Insurance Claims (Overview)
            </Link>
            {page.relatedService && (
              <Link
                to={page.relatedService.href}
                data-testid="underpaid-service-link"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
              >
                {page.relatedService.label}
                <ArrowRight className="h-4 w-4 text-[#E11D26]" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Calculator callout */}
      <CalculatorCallout
        eyebrow={`Was Your ${page.peril} Payment Short?`}
        headline="See What Your Claim Should Actually Pay."
        body="Run your damage through our free calculator to get a defensible recovery range built from current Florida market rates — then compare it to what the carrier paid and let us fight for the difference."
        ctaLabel="Calculate My Shortfall"
        source={`underpaid-${page.slug}`}
      />

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Underpaid {page.peril} Claim FAQ
          </h2>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`underpaid-faq-${i}`}>
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-white/45 text-xs italic">
            This information is general and educational, not legal advice. Statutory
            deadlines and policy provisions vary; consult the current Florida Statutes,
            your policy, or an attorney for your situation.
          </p>
        </div>
      </section>

      {/* Cluster nav — other underpaid claim types */}
      <section className="py-14 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Other Underpaid Claim Types
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`underpaid-cluster-${s.slug}`}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  Underpaid {s.peril} Claim
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            The first check is rarely the last word.
          </h3>
          <Link
            to="/#contact"
            data-testid="underpaid-cta-final"
            className="inline-flex items-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            Recover My Shortfall <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {slug.includes("hurricane") && <NwflInboundLink topic="underpaid" />}

      <Footer />
    </div>
  );
};

export default UnderpaidClaimPage;
